import { Box, Tag, Text } from '@factorialco/deprecated-design-system'
import { PermissionsContextEnum } from 'generated/resources/graphql'
import { uniqWith } from 'lodash'

import CheckBox from 'modules/core/components/Inputs/CheckBox'

import VerbsColumn from 'modules/permissions/components/VerbsColumn'
import { contextLabel } from 'modules/permissions/lib/contexts'

import type { ToggleablePermission } from '../types'

const CONTEXT_VALUES = [
  'owned',
  'reportees',
  'team_leader',
  'legal_entity',
  'everybody',
] satisfies PermissionsContextEnum[]

interface Props {
  permissions: ToggleablePermission[]
  disabled: boolean
}

function VerbsWithContext({ permissions, disabled }: Props) {
  const verbs = uniqWith(permissions, (a, b) => a.verb === b.verb && a.resource === b.resource).map(
    (permission) => ({
      verbLabel: permission.verbLabel,
      description: permission.verbDescription,
      resource: permission.resource,
    })
  )

  verbs.sort((a, b) => a.verbLabel.localeCompare(b.verbLabel))

  // To take advantage of the horizontal real state of desktop displays, we split the verbs into two
  // columns to minimize vertical scrolling. At some point, we should revisit this and make it
  // responsive to provide a single column layout on mobile-like viewports.
  const midPoint = Math.ceil(verbs.length / 2)
  const firstHalf = verbs.slice(0, midPoint)
  const secondHalf = verbs.slice(midPoint)

  const column = (columnVerbs: Verb[]) => (
    <VerbsColumn>
      {columnVerbs.map((verb) => (
        <ContextfulVerbRow
          key={verb.verbLabel}
          permissions={permissions}
          verb={verb}
          disabled={disabled}
        />
      ))}
    </VerbsColumn>
  )

  return (
    <Box flexDirection="row" gap="s32">
      {column(firstHalf)}
      {secondHalf.length > 0 && column(secondHalf)}
    </Box>
  )
}

interface Verb {
  verbLabel: string
  description?: string | null
  resource?: string | null
}

interface ContextfulVerbRowProps {
  verb: Verb
  permissions: ToggleablePermission[]
  disabled: boolean
}

const ContextfulVerbRow = ({ verb, permissions, disabled }: ContextfulVerbRowProps) => {
  const verbPermissions = permissions.filter(
    (perm) => perm.verbLabel === verb.verbLabel && perm.resource === verb.resource
  )

  const resourceTags = verbPermissions[0]?.resourceTags

  return (
    <Box key={`${verb.verbLabel}-${verb.resource}`} width="full" flexDirection="column" gap="s8">
      <Box width="full" gap="s4" flexDirection="column">
        <Box flexDirection="column" gap="s4"></Box>
        <Text.Normal weight="semibold" size="200">
          {verb.verbLabel}
        </Text.Normal>
        <Text.Normal color="grey700" size="200">
          {verb.description}
        </Text.Normal>
        {resourceTags && resourceTags.length > 0 && (
          <Box flexDirection="row" gap="s4">
            {resourceTags.map((tag) => (
              <Tag key={tag.id} inline type="neutral" size="small" ellipsis>
                {tag.label}
              </Tag>
            ))}
          </Box>
        )}
      </Box>
      <PermissionsCheckboxes verbPermissions={verbPermissions} disabled={disabled} />
    </Box>
  )
}

interface PermissionsCheckboxesProps {
  verbPermissions: ToggleablePermission[]
  disabled: boolean
}

function PermissionsCheckboxes({ verbPermissions, disabled }: PermissionsCheckboxesProps) {
  return (
    <Box width="full" flexDirection="column" gap="s4">
      {CONTEXT_VALUES.map((context) => {
        const permission = verbPermissions.find((perm) => perm.context === context)
        if (!permission) {
          return null
        }

        return (
          <ToggleableContext
            key={permission.id}
            context={context}
            disabled={disabled}
            toggleFunction={permission.toggle}
            currentValue={permission.toggled}
          />
        )
      })}
    </Box>
  )
}

interface ToggleableContextProps {
  context: PermissionsContextEnum
  currentValue: boolean
  toggleFunction: () => void
  disabled: boolean
}

function ToggleableContext({
  context,
  currentValue,
  toggleFunction,
  disabled,
}: ToggleableContextProps) {
  return (
    <Box width="fit-content" flexDirection="row" alignItems="center" gap="s4">
      <CheckBox disabled={disabled} onClick={toggleFunction} checked={currentValue} />
      <Text.Normal size="200">{contextLabel(context)}</Text.Normal>
    </Box>
  )
}

export default VerbsWithContext
